import React, { useState, useEffect, useCallback } from 'react';
import { Grid, Paper, Typography, Box, useTheme, Skeleton, Tabs, Tab } from '@mui/material';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    ResponsiveContainer,
    CartesianGrid
} from 'recharts';
// DFINITY agent is now required for the CanisterBalanceChart
// FIX: Changed the import to use a CDN URL to resolve the dependency.
import { HttpAgent, Actor } from "@dfinity/agent";
import { useTranslation } from 'react-i18next';
import BtcBalanceChart from "../../Analytics/BtcBalanceChart.component";


// --- TAB PANEL COMPONENT ---
function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`chart-tabpanel-${ index }`}
            aria-labelledby={`chart-tab-${ index }`}
            style={{ height: '100%', display: value === index ? 'flex' : 'none', flexDirection: 'column' }}
            {...other}
        >
            {value === index && <Box sx={{ pt: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>{children}</Box>}
        </div>
    );
}

function a11yProps(index) {
    return { id: `chart-tab-${ index }`, 'aria-controls': `chart-tabpanel-${ index }` };
}

// --- SHARED COMPONENTS & HELPERS ---
const ChartContainer = ({ children }) => (
    <Paper sx={{ p: 1, height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'transparent' }}>
        <Box sx={{ flex: 1 }}>{children}</Box>
    </Paper>
);

const CustomTooltip = ({ active, payload, label }) => {
    const theme = useTheme();
    if (active && payload?.length) {
        const date = new Date(label);
        const formattedDate = date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
        const value = payload[0].value;

        return (
            <Paper sx={{ p: 1, background: 'rgba(0,0,0,0.85)', border: `1px solid ${ theme.palette.primary.main }50` }}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>{formattedDate}</Typography>
                {payload.map(pld => (
                    <Typography key={pld.dataKey} variant="caption" sx={{ color: pld.color, display: 'block' }}>
                        {pld.name}: {value.toLocaleString(undefined, { maximumFractionDigits: 8 })}
                    </Typography>
                ))}
            </Paper>
        );
    }
    return null;
};

const formatNumber = (num, locale = 'en-US') => num.toLocaleString(locale);
const formatDate = (dateString, locale = 'en-US') => new Date(dateString).toLocaleDateString(locale, { month: 'short', day: 'numeric' });

// --- Candid Interface for the Canister ---
const idlFactory = ({ IDL }) => {
    const Time = IDL.Int;
    const BalanceResult = IDL.Record({ 'balance': IDL.Nat, 'timestamp': Time });
    return IDL.Service({
        'getDailyBuffer': IDL.Func([], [IDL.Vec(BalanceResult)], ['query']),
        'getWeeklyBuffer': IDL.Func([], [IDL.Vec(BalanceResult)], ['query']),
        'getMonthlyBuffer': IDL.Func([], [IDL.Vec(BalanceResult)], ['query']),
        'getYearlyBuffer': IDL.Func([], [IDL.Vec(BalanceResult)], ['query']),
        'getDecadeBuffer': IDL.Func([], [IDL.Vec(BalanceResult)], ['query']),
    });
};


// --- CANISTER BALANCE CHART COMPONENT (NOW WITH LIVE DATA) ---
const CanisterBalanceChart = ({ t }) => {
    const theme = useTheme();
    const [actor, setActor] = useState(null);
    const [historicalData, setHistoricalData] = useState({ type: 'Daily', data: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // --- Actor Initialization ---
    useEffect(() => {
        const canisterId = "ojraw-hyaaa-aaaak-ae53q-cai";
        const host = "https://icp-api.io";
        const agent = new HttpAgent({ host });
        const canisterActor = Actor.createActor(idlFactory, { agent, canisterId });
        setActor(canisterActor);
    }, []);

    // --- Data Fetching from Canister ---
    useEffect(() => {
        const fetchHistoricalData = async () => {
            if (!actor) return;

            setLoading(true);
            setError('');
            try {
                const bufferType = historicalData.type;
                let rawData;
                switch (bufferType) {
                    case 'Weekly': rawData = await actor.getWeeklyBuffer(); break;
                    case 'Monthly': rawData = await actor.getMonthlyBuffer(); break;
                    case 'Yearly': rawData = await actor.getYearlyBuffer(); break;
                    case 'Decade': rawData = await actor.getDecadeBuffer(); break;
                    case 'Daily':
                    default: rawData = await actor.getDailyBuffer(); break;
                }

                // Process data for the chart
                const chartReadyData = rawData.map(item => ({
                    timestamp: Number(item.timestamp / BigInt(1000000)), // ns to ms
                    balance: Number(item.balance) / 1e8 // Convert from e8s
                }));

                setHistoricalData(prev => ({ ...prev, data: chartReadyData }));

            } catch (e) {
                console.error("Failed to fetch canister data:", e);
                setError(t('analytics.failedToFetchBufferData', { bufferType: historicalData.type }));
            } finally {
                setLoading(false);
            }
        };

        fetchHistoricalData();
    }, [actor, historicalData.type, t]);


    const handleFetchHistorical = (bufferType) => {
        // This will trigger the useEffect above to re-fetch data
        setHistoricalData({ type: bufferType, data: [] });
    };

    const formatXAxisTick = (unixTime) => {
        const date = new Date(unixTime);
        switch (historicalData.type) {
            case 'Daily': return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
            case 'Weekly':
            case 'Monthly': return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
            case 'Yearly': return date.toLocaleDateString([], { month: 'short' });
            case 'Decade': return date.getFullYear().toString();
            default: return date.toLocaleDateString();
        }
    };

    return (
        <Paper sx={{ p: 1, height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'transparent' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
                <Tabs
                    value={historicalData.type}
                    onChange={(e, newValue) => handleFetchHistorical(newValue)}
                    aria-label="historical data timeframes"
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        minHeight: 24,
                        '& .MuiTab-root': {
                            minHeight: 24,
                            fontSize: '0.65rem',
                            padding: '4px 8px',
                            minWidth: 'auto',
                        }
                    }}
                >
                    {['Daily', 'Weekly', 'Monthly', 'Yearly', 'Decade'].map(label =>
                        <Tab key={label} label={t(`analytics.${ label.toLowerCase() }`, label)} value={label} />
                    )}
                </Tabs>
            </Box>
            <Box sx={{ flex: 1 }}>
                {loading ? (
                    <Skeleton variant="rectangular" width="100%" height="100%" />
                ) : error ? (
                    <Typography color="error" sx={{ p: 2 }}>{error}</Typography>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={historicalData.data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" />
                            <XAxis dataKey="timestamp" tickFormatter={formatXAxisTick} tick={{ fontSize: 11, fill: theme.palette.text.secondary }} tickLine={false} axisLine={false} />
                            <YAxis domain={['dataMin - 0.1', 'auto']} tickFormatter={(tick) => tick.toFixed(2)} tick={{ fontSize: 11, fill: theme.palette.text.secondary }} tickLine={false} axisLine={false} />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend verticalAlign="top" wrapperStyle={{ top: -5, fontSize: '12px' }} />
                            <Line type="monotone" dataKey="balance" name="Balance (BTC)" stroke={theme.palette.primary.main} strokeWidth={2} dot={false} />
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </Box>
        </Paper>
    );
};


// --- MAIN COMPONENT ---
export default function PlatformCharts() {
    const locale = 'en-US';
    const { t } = useTranslation();
    const theme = useTheme();
    const [odinData, setOdinData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [tabValue, setTabValue] = useState(Math.floor(Math.random() * 3));

    const handleTabChange = (event, newValue) => setTabValue(newValue);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await fetch("https://api.odin.fun/v1/statistics/dashboard");
                if (!res.ok) throw new Error(`API call failed: ${ res.status }`);
                const json = await res.json();
                const dates = json.trades_30d.map(t => t.date).reverse();
                const combinedData = dates.map(date => {
                    const activity = json.activities_30d.find(x => x.date === date) || {};
                    return {
                        date: formatDate(date, locale),
                        withdraw: +(activity.activity_types?.withdraw?.amount_btc / 1e11).toFixed(3),
                        deposit: +(activity.activity_types?.deposit?.amount_btc / 1e11).toFixed(3),
                        buys: activity.activity_types?.buy?.count || 0,
                        sells: activity.activity_types?.sell?.count || 0,
                    };
                });
                setOdinData(combinedData);
            } catch (err) {
                console.error("Failed to fetch Odin data:", err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [locale]);

    if (loading && !odinData) {
        return (
            <Paper sx={{ p: 1, height: '100%', backgroundColor: 'transparent' }}>
                <Skeleton variant="text" width="60%" height={48} />
                <Skeleton variant="rectangular" width="100%" height="calc(100% - 64px)" sx={{ mt: 2 }} />
            </Paper>
        );
    }

    if (error) {
        return <Typography color="error">Failed to load chart data: {error}</Typography>;
    }

    return (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'flex-end' }}>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    aria-label="chart tabs"
                    sx={{
                        minHeight: 24,
                        '& .MuiTab-root': {
                            minHeight: 24,
                            fontSize: '0.65rem',
                            padding: '4px 8px',
                            minWidth: 'auto',
                        }
                    }}
                >
                    <Tab label={t('analytics.depositsVsWithdrawals', 'Deposits vs Withdrawals')} {...a11yProps(0)} />
                    <Tab label={t('analytics.buysVsSells', 'Buys vs Sells')} {...a11yProps(1)} />
                    <Tab label={t('analytics.canisterBalance', 'Canister Balance')} {...a11yProps(2)} />
                    <Tab label={t('analytics.canisterBalanc', 'Activities')} {...a11yProps(3)} />
                </Tabs>
            </Box>
            <TabPanel value={tabValue} index={0}>
                <ChartContainer>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={odinData} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" />
                            <XAxis dataKey="date" tick={{ fontSize: 11, fill: theme.palette.text.secondary }} tickLine={false} axisLine={false} />
                            <YAxis tickFormatter={val => `${ val }`} tick={{ fontSize: 11, fill: theme.palette.text.secondary }} tickLine={false} axisLine={false} />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend verticalAlign="top" wrapperStyle={{ top: -5, fontSize: '12px' }} />
                            <Line type="monotone" dataKey="deposit" name={t('analytics.deposits', 'Deposits')} stroke={theme.palette.success.main} dot={false} strokeWidth={2} />
                            <Line type="monotone" dataKey="withdraw" name={t('analytics.withdrawals', 'Withdrawals')} stroke={theme.palette.error.main} dot={false} strokeWidth={2} />
                        </LineChart>
                    </ResponsiveContainer>
                </ChartContainer>
            </TabPanel>
            <TabPanel value={tabValue} index={1}>
                <ChartContainer>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={odinData} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" />
                            <XAxis dataKey="date" tick={{ fontSize: 11, fill: theme.palette.text.secondary }} tickLine={false} axisLine={false} />
                            <YAxis tickFormatter={(val) => formatNumber(val, locale)} tick={{ fontSize: 11, fill: theme.palette.text.secondary }} tickLine={false} axisLine={false} />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend verticalAlign="top" wrapperStyle={{ top: -5, fontSize: '12px' }} />
                            <Bar dataKey="buys" name={t('analytics.buys', 'Buys')} fill={theme.palette.info.main} radius={[4, 4, 0, 0]} />
                            <Bar dataKey="sells" name={t('analytics.sells', 'Sells')} fill={theme.palette.warning.main} radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartContainer>
            </TabPanel>
            <TabPanel value={tabValue} index={2}>
                <CanisterBalanceChart t={t} />
            </TabPanel>
            <TabPanel value={tabValue} index={3}>
                <BtcBalanceChart t={t} />
            </TabPanel>
        </Box>
    );
}
